
export const cutOff = { mastery: 9, pass: 4, fail: 2, perf_close: 1, perf_far: 2 };

export const generalConceptFeedback = (concept, conceptScores, scoringMethod, sc, isFuture=false) => {
  if (conceptScores && !isFuture){
    let feedback = conceptScoringFeedback(conceptScores.userscore, conceptScores.avg_score, conceptScores.maxscore, scoringMethod, sc)
    return conceptScores && feedback;
  }
  else{
    return "";
  }
}

export const generalConceptFeedbackParts = (concept, conceptScores, scoringMethod, sc, isFuture=false) => {
  if (!conceptScores || isFuture) {
    return null;
  }
  const parts = conceptScoringFeedbackParts(conceptScores.userscore, conceptScores.avg_score, conceptScores.maxscore, scoringMethod, sc);
  return parts.summary || parts.message ? parts : null;
}

export const conceptFeedbackFromConceptData = (concept, conceptScores, goalOrientation) => {
  return conceptScores && (conceptFeedback(conceptScores.userscore, conceptScores.avg_score, conceptScores.maxscore, goalOrientation));
}

export const conceptScoringFeedback = (userScore, avg_score, maxscore, scoringMethod, isSocial) => {
  const { summary, message } = conceptScoringFeedbackParts(userScore, avg_score, maxscore, scoringMethod, isSocial);
  return summary + message;
}

/**
 * Same feedback as conceptScoringFeedback, split into the line explaining how the student is doing (summary)
 * and the feedback message. The trend tells whether the student is ahead/behind the comparison group, if one is shown.
 */
export const conceptScoringFeedbackParts = (userScore, avg_score, maxscore, scoringMethod, isSocial) => {
  if (userScore <= 0) {
    return { summary: "", message: "", trend: null };
  }

  let summary = "";
  let feedback = "";
  const userScorePercent = Math.round(userScore * 100 / maxscore);
  const avgScorePercent = Math.round(avg_score * 100 / maxscore);
  const diff = userScorePercent - avgScorePercent

  switch (scoringMethod) {
    case "knowledge":
      summary += "Your score on this topic is " + userScorePercent + "%. ";
      if (isSocial) {
        summary += "And the average student in the class scores " + avgScorePercent + "%.";
      }
      break;

    case "effort":
      summary += "You have attempted <b>" + Math.round(userScore) + "</b> items on this topic. "
      if (isSocial) {
        summary += "Average student in your class has attempted <b>" +  Math.round(avg_score) + "</b> items.";
      }
      if (userScore >= avg_score) {
        feedback += "Some exercises slightly change on every retry. ";
      }
      feedback += "Keep Going!";
      break;

    case "normal":
      summary += `Your score on this topic is ${userScorePercent}% and the average score of the class is ${avgScorePercent}%. `;
      if (userScorePercent >= 99) { // Student is completely finished.
        feedback += "You have mastered this topic."
      }
      else if (diff > 15) { // Student is leading but not completely finished yet.
        feedback += "Your score is well above the average."
      }
      else if (diff > -15) { // Student is near the class average.
        feedback += "Your score is near the average."
      }
      else { // Student is far behind the class average
        feedback += "Your score is very below the average."
      }
      break;

    case "top25":
      summary += `Your score on this topic is ${userScorePercent}% and the average score of students similar to you is ${avgScorePercent}%. `;
      if (userScorePercent >= 90) { // Student is (almost) completely finished.
        feedback += "Excellent work!"
      }
      else if (diff > 5) { // Student is leading but not completely finished yet.
        feedback += "Great, you are ahead of the group, keep leading the way!"
      }
      else if (diff > -5) { // Student is near the class average.
        feedback += "Good effort, you are moving along steadily with your classmates. Can you challenge yourself to get one step ahead?"
      }
      else if (userScorePercent < 20) { // Student has just started and the average is far away.
        feedback += "That's a start, let's see if you can explore a few more assignments."
      }
      else if (diff > -20) { // Student is a bit behind the class average
        feedback += "This is a good foundation, you are close being right on track with the rest of the class. Why not try completing some other assignments today?"
      }
      else { // Student is far behind the class average
        feedback += "Every assignment you complete is a step forward!"
      }
      break;

    case "bottom25":
      summary += `Your score on this topic is ${userScorePercent}% and the average score of students similar to you is ${avgScorePercent}%. `;
      if (userScorePercent >= 90) { // Student is (almost) completely finished.
        feedback += "Excellent work!"
      }
      else if (diff > 15) { // Student is leading but not completely finished yet.
        feedback += "Great, you are ahead of the group. Keep leading the way!"
      }
      else if (diff > 0) { // Student is near the class average.
        feedback += "Good effort, you are moving along steadily with your classmates. Can you challenge yourself to get one step ahead?"
      }
      else if (userScorePercent < 15) { // Student has just started and the average is far away.
        feedback += "That's a start, let's see if you can explore a few more assignments."
      }
      else if (diff > -10) { // Student is a bit behind the class average
        feedback += "This is a good foundation, you are close being right on track with the rest of the class. Why not try completing some other assignments today?"
      }
      else { // Student is far behind the class average
        feedback += "Every assignment you complete is a step forward!"
      }
      break;

    case "nosc":
      summary += `Your score on this topic is ${userScorePercent}%. `;
      if (userScorePercent >= 90) { // 90-100%
        feedback += "Excellent work!"
      }
      else if (userScorePercent >= 75) { // 75-89%
        feedback += "Good effort, you are close to mastering this topic!"
      }
      else if (userScorePercent >= 45) { // 45-74%
        feedback += "This is a good foundation, keep up the work!"
      }
      else if (userScorePercent >= 20) { // 20-44%
        feedback += "You have completed the first assignments, keep going!"
      }
      else { // 0-19%
        feedback += "That's a start, let's see if you can explore a few more assignments."
      }
      break;

    default:
      break;
  }

  let trend = null;
  if (isSocial && scoringMethod !== "nosc" && avg_score != null) {
    trend = userScore >= avg_score ? "ahead" : "behind";
  }

  return { summary, message: feedback, trend };
}

export const conceptFeedback = (userScore, avg_score, maxscore, goalOrientation) => {
  if (userScore<=0){
    return "";
  }
  const progress_own = ` Progress: ${Math.round(userScore*100/maxscore)}%. `
  if (goalOrientation.includes("performance")){
    const progress_avg = ` Class Avg: ${Math.round(avg_score*100/maxscore)}%. `
    return progress_own + progress_avg;
  }else{
    return progress_own;
  }
};
